import { useContext, useEffect, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Button } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Loan, LoanDoc, LoanDocState, Task, TaskState } from '../../../../gql/graphql';
import { LoanContext, SelectLoanDocumentContext } from '../LoanDetails';
import { useCreateLoanDoc, useDeleteLoanDocument } from '../../../../services/loan/mutations';
import { useDownloadLoanDocsPending, useGetLoanDocByLoan } from '../../../../services/loan/querys';
import { StaticLoanDocument } from './types';
import { filterGuarantors, getExpirationLoanDoc } from './Shared/utils/documentHelpers';
import { keyRefLoanDocToTask } from './constants';
import { sanitizeFileName } from '../../../../@common/utils/helpers/sanitize-doc-name';

interface UseLoanDocumentsProps {
  tasks: Task[];
  setAllDocsComplete: Function;
}

export const useLoanDocuments = ({ tasks, setAllDocsComplete }: UseLoanDocumentsProps) => {
  const loan: Loan | any = useContext(LoanContext);
  const { loanDocKey, setLoanDocKey }: any = useContext(SelectLoanDocumentContext);

  const { execute: createLoanDoc } = useCreateLoanDoc();
  const { execute: deleteLoanDocument } = useDeleteLoanDocument();
  const { data: loanDocs, reexecute: getLoanDocByLoan } = useGetLoanDocByLoan({
    variables: { loanId: loan._id }
  });
  const { data: documentsDownload, reexecute: getDownloadLoanDocsPending } = useDownloadLoanDocsPending({
    variables: { loanId: loan._id },
    pause: !loan._id
  });

  const [loanDocumentsStatic, setLoanDocumentsStatic] = useState<StaticLoanDocument[]>([
    { key: 'term_sheet', label: 'Term Sheet', isDelete: false },
    { key: 'appraisal', label: 'Appraisal', isDelete: false },
    { key: 'pre_approval', label: 'Pre-approval letter', isDelete: false },
    { key: 'title', label: 'Title', isDelete: false },
    { key: 'insurance', label: 'Insurance Documents', isDelete: false },
    { key: 'closing_docs', label: 'Closing docs', isDelete: false },
  ]);
  const [loadingDonwload, setLoadingDownload] = useState<boolean>(false);
  const [selectLoanDoc, setSelectLoanDoc] = useState<any>();

  const newLoanDocs = loanDocs?.filter((item) => !item.key);
  const loanGuarantorsFiltered: any = filterGuarantors(loanDocs || []);

  const getStateLoanDoc = (loanDoc: LoanDoc) => {
    const isTaskKeyRef = keyRefLoanDocToTask[loanDoc?.key || ''];
    const taskRef = tasks.find(task => task.key === isTaskKeyRef);

    const isLoanDoc = loanDoc._id ? loanDoc : loanDocs?.find(item =>
      (loanDoc.ownership && item.name === loanDoc.name) ||
      (!loanDoc.ownership && item.key && item.key === loanDoc.key)
    );

    if (loanDoc.key === 'insurance') {
      const isContact = !!taskRef?.metadata?.email;
      const isObie = taskRef?.metadata?.obieOptionSelected;
      const isDocument = taskRef?.metadata?.documentOptionSelected;

      if (!isLoanDoc && (taskRef?.state === TaskState.Review || taskRef?.state === TaskState.Approved)) {
        if (taskRef?.metadata?.contactedApproved) return 'UPLOAD DOCUMENT'
        else if (isDocument || isContact) return 'MANUAL ACTION'
        else if (isObie) return 'UPLOAD DOCUMENT'
      }
      else if (isLoanDoc && isLoanDoc.state === LoanDocState.Approved) return 'COMPLETE';
      else if (isLoanDoc) return 'VERIFY/UPDATE STATUS';
    }

    if (loanDoc?.key === 'appraisal') {
      if (!isLoanDoc && (taskRef?.state === TaskState.Review || taskRef?.state === TaskState.Approved)) {
        return 'UPLOAD DOCUMENT'
      } else if (isLoanDoc && (isLoanDoc.state === LoanDocState.Pending || isLoanDoc?.state === LoanDocState.Ordered)) {
        return 'UPLOAD DOCUMENT'
      } else if (isLoanDoc && isLoanDoc.state === LoanDocState.Received) {
        return 'VERIFY/UPDATE STATUS'
      } else if (isLoanDoc) return 'COMPLETE'
    }

    if (loanDoc?.key === 'title') {
      if (!isLoanDoc && (taskRef?.state === TaskState.Review || taskRef?.state === TaskState.Approved)) {
        return 'UPLOAD DOCUMENT'
      } else if (isLoanDoc && (isLoanDoc.state === LoanDocState.Pending || isLoanDoc?.state === LoanDocState.Requested)) {
        return 'UPLOAD DOCUMENT'
      } else if (isLoanDoc && isLoanDoc.state === LoanDocState.Verified) {
        return 'VERIFY/UPDATE STATUS'
      } else if (isLoanDoc) return 'COMPLETE'
    }

    if (
      (!isLoanDoc || isLoanDoc?.state === LoanDocState.Blocking) &&
      (taskRef?.state === TaskState.Approved || taskRef?.state === TaskState.Review) &&
      !isLoanDoc?.file && isLoanDoc?.files?.length === 0
    ) {
      return 'UPLOAD DOCUMENT';
    } else if (isLoanDoc?.state === LoanDocState.Blocking) {
      return 'BLOCKED';
    } else if (
      isLoanDoc?.state === LoanDocState.Received ||
      isLoanDoc?.state === LoanDocState.Ordered
    ) {
      return 'VERIFY/UPDATE STATUS';
    } else if (isLoanDoc?.state === LoanDocState.Approved) {
      return 'COMPLETE';
    }
    return !isLoanDoc && taskRef?.state !== TaskState.Approved && taskRef?.state !== TaskState.Review ? 'AWAITING BORROWER'
      : !isLoanDoc?.file && isLoanDoc?.files?.length === 0 ? 'UPLOAD DOCUMENT' : 'COMPLETE';
  };

  useEffect(() => {
    setLoanDocumentsStatic((prev) =>
      prev.map((doc) => {
        const existingDoc = loanDocs?.find((item) => item.key === doc.key);
        if (existingDoc) {
          return {
            ...doc,
            isDelete: existingDoc.isDeleted || false,
          };
        }
        return doc;
      })
    );
  }, [loanDocs]);

  useEffect(() => {
    if (loanDocKey === 'closeAll') {
      setSelectLoanDoc(null);
      setLoanDocKey();
    }

    if (loanDocKey) {
      const isLoanDoc = loanDocumentsStatic.find(item => item.key === loanDocKey);

      if (isLoanDoc) {
        const isTaskKeyRef = keyRefLoanDocToTask[isLoanDoc?.key || ''];
        const taskRef = tasks.find(task => task.key === isTaskKeyRef);

        setSelectLoanDoc({ key: isLoanDoc.key, label: isLoanDoc.label, task: taskRef });
      }
      setLoanDocKey();
    }
  }, [loanDocKey, loanDocumentsStatic, tasks, setLoanDocKey]);

  useEffect(() => {
    const allDocuments = [
      ...loanDocumentsStatic.filter(doc => !doc.isDelete),
      ...(newLoanDocs || []),
      ...loanGuarantorsFiltered
    ];
    const statusMap: Record<string, string> = {};
    allDocuments.forEach((doc, index) => {
      const key = doc.key || doc._id || `doc_${index}`;
      const status = getStateLoanDoc(doc);
      statusMap[key] = status;
    });
    const statuses = Object.values(statusMap);
    const allComplete = statuses.length > 0 && statuses.every(status => status === 'COMPLETE');
    setAllDocsComplete(allComplete);
  }, [loanDocumentsStatic, newLoanDocs, loanGuarantorsFiltered, tasks, loanDocs]);

  const isBoxInfo = (loanDoc: LoanDoc, handlerOpenTask: Function) => {
    let taskRef;

    if (loanDoc.key === 'insurance') {
      taskRef = tasks.find(task => task.key === 'insurance');

      if (loanDoc.key === 'insurance') {
        const isContact = !!taskRef?.metadata?.email;
        const isDocument = taskRef?.metadata?.documentOptionSelected;

        if (taskRef?.state === TaskState.Review) {
          if (isDocument)
            return (
              <>
                <span>The borrower has uploaded their own docs. </span>
                <b className='cursor' onClick={() => handlerOpenTask('insurance')} >View and approve them here</b>{' '}
                <span>to auto-populate this section</span>
              </>
            );
          else if (isContact)
            return (
              <>
                <span>The borrower has provided agent contact info.{' '}</span>
                <Button type="link" className='button-bold' onClick={() => handlerOpenTask('insurance')}>
                  Contact their agent here <ArrowRightOutlined className="cursor" onClick={() => handlerOpenTask('insurance')} />
                </Button>
              </>
            );
        }

        return null;
      }
    }
  };

  const handlerOnBlocking = async (loanDoc: any) => {
    const isLoanDoc = loanDocs?.find(item => item.key === loanDoc.docKey);

    if (isLoanDoc?.state === LoanDocState.Blocking) {
      await deleteLoanDocument({
        loanDocId: isLoanDoc?._id,
        fileKey: loanDoc.docKey,
      });
    } else {
      await createLoanDoc({
        _id: isLoanDoc?._id,
        key: loanDoc.docKey,
        name: loanDoc.label,
        loan: loan._id,
        state: LoanDocState.Blocking
      });
    }

    getLoanDocByLoan({ requestPolicy: 'network-only' });
  };

  const isFile = (loanDoc: LoanDoc) => {
    const isLoanDoc = loanDocs?.find(item => item.key === loanDoc.key);
    return !!isLoanDoc?.file || (isLoanDoc?.files && isLoanDoc.files.length > 0);
  };

  const downloadFile = async (loanDoc: LoanDoc | any) => {
    let isLoanDoc: any = loanDoc._id ? loanDocs?.find(item => item._id === loanDoc._id) : loanDocs?.find(item => item.key === loanDoc.key);
    const token = localStorage.getItem('token');
    const options: any = {
      mode: 'cors',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };

    if (isLoanDoc?.files && isLoanDoc.files.length > 0) {
      try {
        const zip = new JSZip();
        const remoteFiles = isLoanDoc.files.map(async (file: any, index: number) => {
          try {
            const response = await fetch(file.url, options);
            const data = await response.blob();
            const fileName = sanitizeFileName(file.name) || `file-${index + 1}`;
            zip.file(fileName, data);
            return data;
          } catch (error) {
            console.error(`Error al descargar el archivo ${file.url}`, error);
            return null;
          }
        });

        const fetchedFiles = await Promise.all(remoteFiles);

        if (fetchedFiles.every(file => file !== null)) {
          const content = await zip.generateAsync({ type: 'blob' });
          const zipName = `${sanitizeFileName(isLoanDoc.name)}.zip`;
          saveAs(content, zipName);
        } else {
          console.error('Algunos archivos no se pudieron descargar.');
        }
      } catch (error) {
        console.error('Error al crear el archivo ZIP', error);
      }
      return;
    }

    if (isLoanDoc?.file) {
      try {
        const response = await fetch(isLoanDoc.file?.url, options);
        const data = await response.blob();
        const urlObject = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = urlObject;
        link.download = isLoanDoc.name.toLowerCase();
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(urlObject);

        return data;
      } catch (error) {
        console.error(`Error al descargar el archivo ${isLoanDoc.file.url}`, error);
        return null;
      }
    }
  };

  const downloadAllDocuments = async () => {
    if (!documentsDownload || documentsDownload.length === 0) return;
    setLoadingDownload(true);

    const zip = new JSZip();
    const token = localStorage.getItem('token');
    const options: any = {
      mode: 'cors',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };

    const remoteFiles = documentsDownload.map(async (file, index) => {
      if (!file?.file?.url || !file?.file?.name) return null;
      try {
        const response = await fetch(file.file.url, options);
        const data = await response.blob();
        const fileName = `${index + 1}-${sanitizeFileName(file.file.name)}`;
        zip.file(fileName, data);
        return data;
      } catch (error) {
        console.error(`Failed to fetch ${file.file.url}`, error);
        return null;
      }
    });

    const fetchedFiles = await Promise.all(remoteFiles);
    const successfulFiles = fetchedFiles.filter(f => f !== null);

    if (fetchedFiles.length > 0 && successfulFiles.length === fetchedFiles.length) {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        saveAs(content, 'documents-in-approved-status.zip');
        setLoadingDownload(false);
      });
    } else {
      console.error(`Failed to download ${fetchedFiles.length - successfulFiles.length} of ${fetchedFiles.length} files`);
      setLoadingDownload(false);
    }
  };

  const onDeleteLoanDocument = async (docId?: string, docKey?: string) => {
    if (!docId && !docKey) return;
    if (docKey && loanDocumentsStatic.find(item => item.key === docKey)) {
      const existingDoc = loanDocs?.find((item) => item.key === docKey);
      if (existingDoc) {
        await createLoanDoc({
          _id: existingDoc?._id,
          key: existingDoc.key,
          name: existingDoc.name,
          loan: loan._id,
          isDeleted: true
        });
      } else {
        await createLoanDoc({
          key: docKey,
          name: loanDocumentsStatic.find(item => item.key === docKey)?.label,
          loan: loan._id,
          isDeleted: true,
          state: LoanDocState.Close
        });
      }
      setLoanDocumentsStatic((prev) =>
        prev.map((doc) => (doc.key === docKey ? { ...doc, isDelete: true } : doc))
      );
    } else if (docId) {
      await deleteLoanDocument({ loanDocId: docId, fileKey: docKey });
    }
    getLoanDocByLoan({ requestPolicy: 'network-only' });
    getDownloadLoanDocsPending({ requestPolicy: 'network-only' });
  };

  const handlerSelectLoanDoc = (loanDoc: any) => {
    const isTaskKeyRef = keyRefLoanDocToTask[loanDoc?.key || ''];
    let taskRef = tasks.find(task => task.key === isTaskKeyRef);
    setSelectLoanDoc({ ...loanDoc, task: taskRef });
  };

  return {
    loanDocumentsStatic,
    selectLoanDoc,
    setSelectLoanDoc,
    loadingDonwload,

    loanDocs,
    newLoanDocs,
    loanGuarantorsFiltered,

    getStateLoanDoc,
    isBoxInfo,
    handlerOnBlocking,
    isFile,
    downloadFile,
    downloadAllDocuments,
    onDeleteLoanDocument,
    handlerSelectLoanDoc,
    getExpirationLoanDoc: (loanDoc: LoanDoc) => getExpirationLoanDoc(loanDoc, loanDocs),
  };
};